import { useState } from 'react';
import { spyScanCooldownRemainingSeconds } from '@kaeliux/game-engine';
import { scanForSpies } from '../services/spyScan.service';
import { usePlanetStore } from '../store/planetStore';
import { useNow } from './useNow';
import { formatCountdown } from '../utils/formatters';

interface SpyScanState {
    scan: () => Promise<void>;
    isScanning: boolean;
    /** Result summary or error text — null until a scan has run. */
    message: string | null;
    /** True while the cooldown or an in-flight request should block the button. */
    disabled: boolean;
    /** Button label, including the live cooldown countdown. */
    label: string;
}

/**
 * Counter-espionage scan behaviour, shared by the Planet page and the
 * Sciences page's Intelligence row — the two places the scan is offered.
 * The scanned planet comes back on the response, so the store is updated
 * straight from it — both pages read `planetStore.currentPlanet` directly, so
 * they see the new `lastSpyScanAt` and start counting down without a refetch.
 */
export function useSpyScan(planetId: string | undefined, intelligenceLevel: number, lastSpyScanAt?: Date): SpyScanState {
    const [isScanning, setIsScanning] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const now = useNow();

    const cooldownRemaining = spyScanCooldownRemainingSeconds(lastSpyScanAt, intelligenceLevel, new Date(now));

    async function scan() {
        if (!planetId || isScanning) return;
        setIsScanning(true);
        setMessage(null);
        try {
            const result = await scanForSpies(planetId);
            usePlanetStore.setState({ currentPlanet: result.planet });
            const found = result.discovered.length;
            const destroyed = result.discovered.filter((d) => d.destroyed).length;
            setMessage(
                found === 0
                    ? 'No spy ships detected.'
                    : `Detected ${found} spy ship${found === 1 ? '' : 's'}${destroyed > 0 ? `, destroyed ${destroyed}` : ''}.`
            );
        } catch (err) {
            setMessage(err instanceof Error ? err.message : 'Failed to scan for spy ships.');
        } finally {
            setIsScanning(false);
        }
    }

    const label = isScanning
        ? 'Scanning…'
        : cooldownRemaining > 0
            ? `Scan for Spy Ships (${formatCountdown(cooldownRemaining * 1000)})`
            : 'Scan for Spy Ships';

    return { scan, isScanning, message, disabled: isScanning || cooldownRemaining > 0 || !planetId, label };
}
